// CrmTable — God's-eye schema-driven grid with frozen columns + horizontal scroll.
import { useState, useEffect } from 'react';
import { parseSectors } from './sectors';

function domainOf(url) {
  if (!url) return null;
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || null;
}
function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

// Stable per-name avatar colour (Gmail-style): same name -> same colour.
const AVATAR_COLOURS = [
  '#7B1E2B', '#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C',
  '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E',
  '#7C5CFC', '#0F766E', '#B91C1C', '#5D4037', '#3949AB', '#00838F',
];
function avatarColour(name) {
  const s = String(name || '?');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLOURS[h % AVATAR_COLOURS.length];
}
function Favicon({ name, website }) {
  const [failed, setFailed] = useState(false);
  const dom = domainOf(website);
  if (!dom || failed) {
    return <span className="crmt-fav" style={{ background: 'linear-gradient(135deg,#FF7A59,#EC4899)' }}>{initials(name)}</span>;
  }
  return <span className="crmt-fav"><img src={`https://www.google.com/s2/favicons?domain=${dom}&sz=64`} alt="" onError={() => setFailed(true)} /></span>;
}
function scoreClass(v) { const s = Number(v) || 0; return s >= 8 ? 'crmt-sc-hot' : s >= 5 ? 'crmt-sc-warm' : 'crmt-sc-cold'; }

const SIGNAL = {
  funding: { c: '#056F4E', bg: '#D1F0E4', l: 'Funding' },
  leadership_change: { c: '#5B3FD6', bg: '#E7E1FD', l: 'Leadership' },
  hiring: { c: '#0B5BB5', bg: '#D9E9FB', l: 'Hiring' },
  tech_change: { c: '#066576', bg: '#CFF0F6', l: 'Tech' },
  news: { c: '#3F4956', bg: '#E3E7ED', l: 'News' },
};
const SEQ = { approved: { c: 'crmt-p-green', l: 'Approved' }, pending: { c: 'crmt-p-amber', l: 'Pending' } };
const STATUS_PILL = { active: { c: 'crmt-p-green', l: 'Active' }, new: { c: 'crmt-p-blue', l: 'New' }, archived: { c: 'crmt-p-slate', l: 'Archived' } };
// Inline-editable columns: key -> { field (DB column), type }. contact_types handled separately via popover.
const EDITABLE_FIELDS = {
  job_title:    { field: 'job_title',    type: 'text' },
  email:        { field: 'email',        type: 'text' },
  phone:        { field: 'phone',        type: 'text' },
  linkedin_url: { field: 'linkedin_url', type: 'text' },
  status:       { field: 'status',       type: 'select', options: ['new', 'active', 'archived'] },
};
const CRM_PILL = { success: { c: 'crmt-p-green', l: 'Pushed' }, pending: { c: 'crmt-p-amber', l: 'Pending' }, failed: { c: 'crmt-p-rose', l: 'Failed' } };

function ukDate(v) {
  if (!v) return null;
  const d = new Date(v); if (isNaN(d)) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function Cell({ col, row, allTypes, onTypeToggle, onCellSave, onContactClick }) {
  const [typePopOpen, setTypePopOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  useEffect(() => {
    if (!typePopOpen) return;
    const close = () => setTypePopOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [typePopOpen]);
  const v = row[col.key];

  const editCfg = onCellSave && EDITABLE_FIELDS[col.key];
  if (editCfg && editing) {
    const commit = () => { setEditing(false); if (draft !== (v ?? '')) onCellSave(row.id, editCfg.field, draft); };
    if (editCfg.type === 'select') {
      return (
        <select
          className="crmt-celledit"
          autoFocus
          value={draft}
          onClick={e => e.stopPropagation()}
          onChange={e => { setDraft(e.target.value); }}
          onBlur={commit}
        >
          {editCfg.options.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
        </select>
      );
    }
    return (
      <input
        className="crmt-celledit"
        autoFocus
        value={draft}
        onClick={e => e.stopPropagation()}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setEditing(false); } }}
      />
    );
  }

  const cellInner = (() => {
  switch (col.render) {
    case 'id':
      return <span className="crmt-id">#{row.id}</span>;
    case 'name': {
      const nm = row.full_name || [row.first_name, row.last_name].filter(Boolean).join(' ') || 'Unnamed';
      return (
        <div className="crmt-cname">
          <span className="crmt-fav" style={{ background: avatarColour(nm) }}>{row.photo_url ? <img src={row.photo_url} alt="" className="crmt-fav-img" onError={(e) => { e.target.style.display = 'none'; }} /> : initials(nm)}</span>
          <div className="crmt-namebox">
            {onContactClick
              ? <button type="button" className="crmt-nm crmt-nm-link" onClick={() => onContactClick(row)}>{nm}</button>
              : <div className="crmt-nm">{nm}</div>}
          </div>
        </div>
      );
    }
    case 'types': {
      const types = Array.isArray(v) ? v : [];
      const assignedIds = new Set(types.map(t => t.id));
      const editable = Array.isArray(allTypes) && typeof onTypeToggle === 'function';
      return (
        <div className="crmt-typecell" onClick={e => { if (editable) { e.stopPropagation(); setTypePopOpen(o => !o); } }}>
          <span className="crmt-chips">
            {types.length === 0 && !editable && <span className="crmt-dash">—</span>}
            {types.length === 0 && editable && <span className="crmt-typeempty">+ Add type</span>}
            {types.slice(0, 3).map(t => (
              <span key={t.id} className="crmt-chip" style={{ color: t.colour, background: (t.colour || '#7C5CFC') + '22' }}>{t.name}</span>
            ))}
            {types.length > 3 ? <span className="crmt-chip crmt-chip-more">+{types.length - 3}</span> : null}
            {editable && types.length > 0 && <span className="crmt-typeadd" title="Edit types">+</span>}
          </span>
          {editable && typePopOpen && (
            <div className="crmc-typepop" onClick={e => e.stopPropagation()}>
              {allTypes.length === 0 && <div className="crmc-typepop-empty">No types yet. Create one via the Types button.</div>}
              {allTypes.map(t => {
                const on = assignedIds.has(t.id);
                return (
                  <div key={t.id} className="crmc-typepop-item" onClick={() => onTypeToggle(row.id, t.id, on)}>
                    <span className="crmc-typepop-dot" style={{ background: t.colour }} />
                    <span>{t.name}</span>
                    {on && <span className="crmc-typepop-check">✓</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    case 'company':
      return v ? <div className="crmt-cname"><Favicon name={v} website={row.website} /><span className="crmt-ctext">{v}</span></div> : <span className="crmt-dash">—</span>;
    case 'companyname': {
      const cn = v || row.name || 'Unnamed';
      return (
        <div className="crmt-cname">
          <Favicon name={cn} website={row.website} />
          <div className="crmt-namebox">
            {onContactClick
              ? <button type="button" className="crmt-nm crmt-nm-link" onClick={() => onContactClick(row)}>{cn}</button>
              : <div className="crmt-nm">{cn}</div>}
          </div>
        </div>
      );
    }
    case 'countpill': {
      const n = v != null && v !== '' ? Number(v) : 0;
      return <span className={`crmt-pill ${n ? 'crmt-p-blue' : 'crmt-p-slate'}`} title={`${n}`}>{n}</span>;
    }
    case 'sweep': {
      let status = row.sweep_status;
      if (!status) {
        if (!row.last_swept_at) status = 'never';
        else if (row.next_sweep_due && new Date(row.next_sweep_due) > new Date()) status = 'fresh';
        else status = 'due';
      }
      const SW = { fresh: { c: 'crmt-p-green', l: 'Up to date' }, due: { c: 'crmt-p-amber', l: 'Needs scan' }, never: { c: 'crmt-p-slate', l: 'Not scanned' } };
      const s = SW[status] || SW.never;
      return <span className={`crmt-pill ${s.c}`}>{s.l}</span>;
    }
    case 'score':
      return v != null && v !== '' ? <span className={`crmt-score ${scoreClass(v)}`}>{v}</span> : <span className="crmt-dash">—</span>;
    case 'number':
      return v != null && v !== '' ? <span>{v}</span> : <span className="crmt-dash">—</span>;
    case 'email':
      return v ? <a className="crmt-link" href={`mailto:${v}`} onClick={e => e.stopPropagation()}>{v}</a> : <span className="crmt-dash">—</span>;
    case 'link':
      return v ? <a className="crmt-link" href={v} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>View</a> : <span className="crmt-dash">—</span>;
    case 'seqstatus': {
      if (!v) return <span className="crmt-pill crmt-p-slate">None</span>;
      const p = SEQ[String(v).toLowerCase()] || { c: 'crmt-p-slate', l: v };
      return <span className={`crmt-pill ${p.c}`}>{p.l}</span>;
    }
    case 'seqcount': {
      const total = row[col.key + '_total'] || 0;
      const approved = row[col.key + '_approved'] || 0;
      if (!total) return <span className="crmt-pill crmt-p-slate">None</span>;
      const cls = approved === total ? 'crmt-p-green' : approved > 0 ? 'crmt-p-amber' : 'crmt-p-slate';
      return <span className={`crmt-pill ${cls}`} title={`${approved} of ${total} approved`}>{approved}/{total}</span>;
    }
    case 'sectors': {
      const secs = parseSectors(v);
      if (!secs.length) return <span className="crmt-dash">—</span>;
      const SEC_COLOURS = ['#5B3FD6', '#0B5BB5', '#056F4E', '#B25400', '#066576', '#A21C5A', '#3F4956', '#7A4D00'];
      const colourOf = (label) => { let h = 0; for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0; return SEC_COLOURS[h % SEC_COLOURS.length]; };
      return (
        <span className="crmt-chips">
          {secs.slice(0, 3).map((s, i) => { const c = colourOf(s); return <span key={i} className="crmt-chip" style={{ color: c, background: c + '1A' }}>{s}</span>; })}
          {secs.length > 3 ? <span className="crmt-chip crmt-chip-more">+{secs.length - 3}</span> : null}
        </span>
      );
    }
    case 'signals': {
      const types = Array.isArray(v) ? v : [];
      if (!types.length) return <span className="crmt-dash">—</span>;
      return (
        <span className="crmt-chips">
          {types.slice(0, 3).map((t, i) => {
            const s = SIGNAL[t] || { c: '#64748B', bg: '#EEF1F5', l: t };
            return <span key={i} className="crmt-chip" style={{ color: s.c, background: s.bg }}>{s.l}</span>;
          })}
          {types.length > 3 ? <span className="crmt-chip crmt-chip-more">+{types.length - 3}</span> : null}
        </span>
      );
    }
    case 'optout':
      return v ? <span className="crmt-pill crmt-p-rose">Opted out</span> : <span className="crmt-dash">—</span>;
    case 'campaign_pill':
      return row.in_campaign && v ? <span className="crmt-pill crmt-p-cyan">{v}</span> : <span className="crmt-dash">—</span>;
    case 'batches': {
      const names = Array.isArray(v) ? v.filter(Boolean) : [];
      if (!names.length) return <span className="crmt-dash">—</span>;
      return (
        <span className="crmt-chips">
          {names.slice(0, 2).map((n, i) => <span key={i} className="crmt-chip crmt-chip-slate">{n}</span>)}
          {names.length > 2 ? <span className="crmt-chip crmt-chip-more">+{names.length - 2}</span> : null}
        </span>
      );
    }
    case 'status': {
      const p = STATUS_PILL[String(v || 'new').toLowerCase()] || STATUS_PILL.new;
      return <span className={`crmt-pill ${p.c}`}>{p.l}</span>;
    }
    case 'crm': {
      if (!v) return <span className="crmt-dash">—</span>;
      const p = CRM_PILL[String(v).toLowerCase()] || { c: 'crmt-p-slate', l: v };
      return <span className={`crmt-pill ${p.c}`}>{p.l}</span>;
    }
    case 'bool':
      return v ? <span className="crmt-tick">✓</span> : <span className="crmt-dash">—</span>;
    case 'ukdate': {
      const d = ukDate(v);
      return d ? <span className="crmt-date">{d}</span> : <span className="crmt-dash">—</span>;
    }
    case 'date': {
      const d = ukDate(v);
      return d ? <span className="crmt-date">{d}</span> : <span className="crmt-dash">—</span>;
    }
    case 'text':
    default:
      return v ? <span className="crmt-ctext">{v}</span> : <span className="crmt-dash">—</span>;
  }
  })();

  if (editCfg) {
    return (
      <div
        className="crmt-editable"
        title="Click to edit"
        onClick={e => { e.stopPropagation(); setDraft(v ?? ''); setEditing(true); }}
      >
        {cellInner}
        <span className="crmt-editpencil">✎</span>
      </div>
    );
  }
  return cellInner;
}

export default function CrmTable({ columns, rows, loading, selectedIds, onToggle, onToggleAll, onContactClick, allTypes, onTypeToggle, onCellSave, sort, onSort }) {
  const allSelected = rows.length > 0 && selectedIds && rows.every(r => selectedIds.has(r.id));
  let leftAcc = 38;
  const leftOffsets = {};
  columns.forEach(col => { if (col.frozen) { leftOffsets[col.key] = leftAcc; leftAcc += col.width; } });

  return (
    <div className="crmt-scroll">
      <table className="crmt-table crmt-godseye">
        <thead>
          <tr>
            <th className="crmt-checkcol crmt-frozen" style={{ left: 0 }}>
              <input type="checkbox" checked={!!allSelected} onChange={onToggleAll} />
            </th>
            {columns.map(col => {
              const sortable = !!col.sort && typeof onSort === 'function';
              const active = sortable && sort && sort.field === col.sort;
              return (
                <th key={col.key} style={{ width: col.width, minWidth: col.width, ...(col.frozen ? { left: leftOffsets[col.key], position: 'sticky', zIndex: 3 } : {}) }} className={col.frozen ? 'crmt-frozen' : undefined}>
                  {sortable ? (
                    <button type="button" className={'crmt-sortbtn' + (active ? ' crmt-sortbtn--on' : '')} onClick={() => onSort(col.sort)}>
                      {col.label}
                      <span className="crmt-sortarrow">{active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </button>
                  ) : col.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length + 1} className="crmt-empty">Loading contacts…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length + 1} className="crmt-empty">No contacts match. Adjust filters or add a contact.</td></tr>
          ) : rows.map(row => {
            const sel = selectedIds && selectedIds.has(row.id);
            return (
              <tr key={row.id} className={sel ? 'crmt-rowsel' : undefined}>
                <td className="crmt-checkcol crmt-frozen" style={{ left: 0 }} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={!!sel} onChange={() => onToggle && onToggle(row.id)} />
                </td>
                {columns.map(col => (
                  <td key={col.key} style={{ width: col.width, minWidth: col.width, ...(col.frozen ? { left: leftOffsets[col.key], position: 'sticky', zIndex: 1 } : {}) }} className={col.frozen ? 'crmt-frozen' : undefined}>
                    <Cell col={col} row={row} allTypes={allTypes} onTypeToggle={onTypeToggle} onCellSave={onCellSave} onContactClick={onContactClick} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
